import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Plus } from 'lucide-react'

import { ReferralBenefits } from './ReferralBenefits'
import { ReferralTable } from './ReferralTable'
import { Currency } from '../../../common/models'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { getCurrencies } from '../../../core/services/festival-settings'
import { useAppSelector } from '../../../store'
import { Header, PageTitle } from '@/components'
import {
  Button,
  Card,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { Icon } from '@/common/components/Icon'

export const ReferralsPage: React.FC = () => {
  const { extraParams, setExtraParams } = useTableParams({
    extraParams: {
      key: 'all',
    },
  })
  const { hasEditPermission } = useCheckPermission({ page: 'REFERRALS' })

  const [addNew, setAddNew] = useState<boolean>(false)
  const [key, setKey] = useState<string>(extraParams.key || 'all')
  const [currency, setCurrency] = useState<Currency | null>(null)
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const getCurrency = useCallback(async () => {
    try {
      const response = await getCurrencies()
      if (response.status === 200) {
        setCurrency(response.data.defaultCurrency)
      }
    } catch (e) {
      console.error(e)
    }
  }, [])

  useEffect(() => {
    getCurrency()
  }, [getCurrency, editionId, festivalName])

  return (
    <>
      <Header
        pageTitle="Referrals"
        actions={
          hasEditPermission &&
          key !== 'rb' && (
            <Button
              variant="primary-bordered"
              onClick={() => {
                setAddNew(true)
              }}
            >
              Add New
              <Icon icon="plus" width="18" height="18" viewBox="0 0 18 18" />
            </Button>
          )
        }
      />
      <Tabs
        value={key}
        onValueChange={(value) => {
          setKey(value)
          setExtraParams((s) => ({ ...s, key: value }))
        }}
        className="w-full"
      >
        <TabsList className="grid w-full h-auto mb-2">
          <div className="bg-white/10 p-2.5 rounded-[20px]">
            <TabsTrigger value="all">All users</TabsTrigger>
            <TabsTrigger value="su">Single User</TabsTrigger>
            <TabsTrigger value="rb">Referral Benefits</TabsTrigger>
          </div>
        </TabsList>
        <Card className="!bg-monochrome4-60 p-5 backdrop-blur-lg max-h-[calc(100vh-200px)] overflow-y-auto">
          <PageTitle title="Rewards" subtitle="Referrals" className="mb-5" />

          <TabsContent value="all">
            <ReferralTable
              visible={addNew}
              type="all"
              setVisible={setAddNew}
              currency={currency}
            />
          </TabsContent>

          <TabsContent value="su">
            <ReferralTable
              visible={addNew}
              type="single"
              setVisible={setAddNew}
              currency={currency}
            />
          </TabsContent>

          <TabsContent value="rb">
            <ReferralBenefits />
          </TabsContent>
        </Card>
      </Tabs>
    </>
  )
}
